import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Card } from '../../shared/components';
import { Books, booksApi, errorText, fmtDate, Integrity } from '../accounting.api';
import BooksHeader from '../components/BooksHeader';
import { useBooksCompany } from '../components/CompanySelect';

const CHECKS: [keyof Omit<Integrity, 'ok'>, string][] = [
  ['unbalancedVouchers', 'Vouchers whose debits and credits differ'],
  ['balanceMismatches', 'Ledger balances that differ from their postings'],
  ['lineMismatches', 'Voucher lines out of step with their voucher'],
];

/**
 * The books' two dates (admin): the beginning date, which every opening balance is as on and
 * which is fixed once a voucher exists, and the lock date, up to which nothing can be entered,
 * altered or cancelled. Also the integrity check of the stored balances.
 */
export default function BooksSettingsPage() {
  const { companies, companyId, setCompanyId, loaded } = useBooksCompany();
  const [books, setBooks] = useState<Books | null>(null);
  const [begin, setBegin] = useState('');
  const [lock, setLock] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [integrity, setIntegrity] = useState<Integrity | null>(null);

  const show = (b: Books) => {
    setBooks(b);
    setBegin(b.booksBeginDate);
    setLock(b.lockedUntil ?? '');
  };

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    setIntegrity(null);
    booksApi
      .get(companyId)
      .then(show)
      .catch((e) => setError(errorText(e, 'Could not load the books', 'ACCOUNTING_BOOKS_FAILED')))
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(load, [load]);

  const save = async () => {
    if (!companyId || !books || !begin) return;
    setBusy('save');
    try {
      show(await booksApi.save(companyId, { booksBeginDate: begin, lockedUntil: lock || null }));
      toast.success('Books saved.');
    } catch (e) {
      toast.error(errorText(e, 'Could not save the books', 'ACCOUNTING_BOOKS_SAVE_FAILED'));
    } finally {
      setBusy(null);
    }
  };

  const check = async () => {
    if (!companyId) return;
    setBusy('check');
    try {
      setIntegrity(await booksApi.integrity(companyId));
    } catch (e) {
      toast.error(errorText(e, 'Could not run the check', 'ACCOUNTING_INTEGRITY_FAILED'));
    } finally {
      setBusy(null);
    }
  };

  const dirty = !!books && (begin !== books.booksBeginDate || (lock || null) !== (books.lockedUntil ?? null));

  return (
    <Stack gap={3}>
      <BooksHeader title='BOOKS SETTINGS' companies={companies} companyId={companyId} onCompany={setCompanyId} loaded={loaded} />
      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}

      {books && (
        <Card title='Dates'>
          <Stack gap={2.5}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Stack gap={1}>
                <TextField
                  label='Books begin on'
                  type='date'
                  size='small'
                  value={begin}
                  disabled={books.hasVouchers}
                  onChange={(e) => setBegin(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Typography variant='caption' color='text.secondary'>
                  {books.hasVouchers
                    ? 'Fixed: vouchers have been entered. Opening balances are as on this date.'
                    : 'Opening balances are as on this date. It can change until the first voucher is entered.'}
                </Typography>
              </Stack>
              <Stack gap={1}>
                <Stack direction='row' gap={1} alignItems='center'>
                  <TextField
                    label='Locked up to'
                    type='date'
                    size='small'
                    value={lock}
                    onChange={(e) => setLock(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                  <Button size='small' onClick={() => setLock('')} disabled={!lock} startIcon={<Icon icon='mdi:lock-open-variant-outline' />}>
                    Clear
                  </Button>
                </Stack>
                <Typography variant='caption' color='text.secondary'>
                  {lock
                    ? `No voucher dated on or before ${fmtDate(lock)} can be entered, altered or cancelled.`
                    : 'Not locked: every date from the beginning is open.'}
                </Typography>
              </Stack>
            </Box>
            <Stack direction='row' justifyContent='flex-end' gap={1}>
              <Button onClick={() => show(books)} disabled={!dirty || busy !== null}>
                Reset
              </Button>
              <Button variant='contained' onClick={save} disabled={!dirty || !begin || busy !== null}>
                {busy === 'save' ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </Card>
      )}

      {books && (
        <Card
          title='Integrity'
          action={
            <Button variant='outlined' startIcon={<Icon icon='mdi:shield-check-outline' />} onClick={check} disabled={busy !== null}>
              {busy === 'check' ? 'Checking…' : 'Check'}
            </Button>
          }
        >
          <Stack gap={1.5}>
            <Typography variant='body2' color='text.secondary'>
              Recomputes every ledger balance from its postings and compares it with what is stored, and checks every voucher still
              balances.
            </Typography>
            {integrity &&
              (integrity.ok ? (
                <Alert severity='success' icon={<Icon icon='mdi:check-circle' />}>
                  All good: every voucher balances and every stored balance matches its postings.
                </Alert>
              ) : (
                CHECKS.filter(([k]) => integrity[k].length > 0).map(([k, label]) => (
                  <Alert key={k} severity='error'>
                    <Typography variant='body2' fontWeight={700}>
                      {label} ({integrity[k].length})
                    </Typography>
                    <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
                      {integrity[k].map((x, i) => (
                        <li key={i}>
                          <Typography variant='body2'>{x}</Typography>
                        </li>
                      ))}
                    </Box>
                  </Alert>
                ))
              ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
